import { z } from "zod"
import { ModelId } from "@/lib/models/model-list"
import { Message } from "@/lib/types"
import { ToolName } from "@/lib/tools/tools"
import { User } from "@/lib/types/user-types"
import {
    type ToolSet,
    type ModelMessage,
    type GenerateTextResult,
    type FinishReason,
    type LanguageModelUsage,
    type CallWarning,
    type StepResult,
    type LanguageModelRequestMetadata,
    type LanguageModelResponseMetadata,
    type AssistantModelMessage,
    type ToolModelMessage,
    type ProviderMetadata,
    type GeneratedFile,
    type TypedToolResult,
    type TypedToolCall,
} from 'ai'
// Send new message and request options



export interface SendNewMessage {
    message: Message
    onSuccess?: () => void
    onFailure?: (error: string) => void
}


export interface CreateNewRequest {
    userMessage: Message,
    onSuccess?: () => void,
    onFailure?: (error: string) => void
}





////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Usage
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface BalanceEvent {
    userId: string
    requestId: string
    amount: number 
    description: `${'input' | 'output'} - ${string}`
    previousBalance: number
    newBalance: number
    profitMultiplier: number
}

export interface AmountUsage {
    inputAmount: number | null
    outputAmount: number | null
    totalAmount: number
    profitMultiplier: number
}

export interface Usage {
    inputTokens: number
    completionTokens: number
    reasoningTokens: number
    outputTokens: number

    inputImages: number
    outputImages: number
    outputMegapixels: number

    calculatedInputAmount: number
    calculatedCompletionAmount: number
    calculatedReasoningAmount: number
    calculatedOutputAmount: number
    calculatedTotalAmount: number
}

export interface ProviderUsageResult {
    amountUsage: AmountUsage
    providerUsage: Partial<Usage>
    metadata: any
}











////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Generation Types
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface ToolInvocation {
    toolName: string
    toolCallId: string
    toolCall: string
    toolResult: string
    status: 'started' | 'completed' | 'failed'
    usage: {
        inputTokens: number
        outputTokens: number
        totalTokens: number
    }
    error: string
    duration: number
}


// Base Tool interface
export interface Tool<TParams, TResult> {
    name: string
    description: string
    parameters: z.ZodSchema<TParams>
    execute: (params: TParams) => Promise<TResult>
}







////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Formatted Request Object for use-manager
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// Base input object schema with common fields
const BaseInputObjectSchema = z.object({
    messages: z.array(z.custom<ModelMessage>()),
    modelId: z.custom<ModelId>(),
    urlTracking: z.string(),
    parameters: z.object({
        maxOutputTokens: z.number().optional(),
        temperature: z.number().optional(),
        topP: z.number().optional(),
        seed: z.number().optional(),
        maxRetries: z.number().optional(),
        abortSignal: z.custom<AbortSignal>().optional(),
        headers: z.record(z.string(), z.string()).optional(),
    }).optional(),
    userId: z.string().optional(),
    chunkingRegex: z.string().optional(), 
    conversationSystemPrompt: z.string().optional()
})

// Mode-specific input object schemas
const StreamTextInputObjectSchema = BaseInputObjectSchema.extend({
    mode: z.literal('stream-text'),
    tools: z.array(z.custom<ToolName>()).optional(),
    maxSteps: z.number().optional(),
})

const GenerateTextInputObjectSchema = BaseInputObjectSchema.extend({
    mode: z.literal('generate-text'),
    tools: z.array(z.custom<ToolName>()).optional(),
    maxSteps: z.number().optional(),
})

const GenerateObjectInputObjectSchema = BaseInputObjectSchema.extend({
    mode: z.literal('generate-object'),
    schema: z.custom<z.ZodSchema>(),
})

const ImageInputObjectSchema = BaseInputObjectSchema.extend({
    mode: z.literal('generate-image'),
    // Minimal image input payload; prompt carried directly instead of messages/history
    prompt: z.string().optional(),
    // Optional source image URL for image-to-image models
    imageUrl: z.string().url().optional(),
})

const GenerateInputObjectSchema = BaseInputObjectSchema.extend({
    mode: z.literal('generate'),
    tools: z.array(z.custom<ToolName>()).optional(),
    maxSteps: z.number().optional(),
})


export const FormattedRequestObjectSchema = z.object({
    assistantMessage: z.custom<Message>(),
    userMessage: z.custom<Message>(),
    conversationId: z.string(),
    inputObject: z.discriminatedUnion('mode', [
        StreamTextInputObjectSchema,
        GenerateTextInputObjectSchema,
        GenerateObjectInputObjectSchema,
        ImageInputObjectSchema,
        GenerateInputObjectSchema,
    ]),
    timeLogs: z.array(z.object({
        eventName: z.string(),
        createdAt: z.date()
    })),
    clientData: z.object({
        ip: z.string().optional(),
        fingerprint: z.string().optional(),
        localStorageId: z.string().optional(),
        sessionStorageId: z.string().optional(),
        cookieId: z.string().optional(),
        sqlDbId: z.string().optional(), 
        forwardedFor: z.array(z.string()).optional(),
        realIp: z.string().optional(),
        cfConnectingIp: z.string().optional(),
        clientIp: z.string().optional(),
        longitude: z.string().optional(),
        latitude: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        vercelIp: z.string().optional()
    }), 
    metadata: z.record(z.any()).optional()
})

export type FormattedRequestObject = z.infer<typeof FormattedRequestObjectSchema>







export interface RequestInformation {
    requestId: string
    responseAPI: any
    inputObject: FormattedRequestObject['inputObject']
    assistantMessage: Message
    usage: Partial<Usage>
    user: User // Now guaranteed to exist after factory authentication
    conversationId?: string
    title?: string
    toolInvocations?: ToolInvocation[]
}

export interface GenerationResponse {
    response: {
        user?: User
        assistantMessage: Message
    }
    title?: string
    //...
}



// Conditional response type based on input object mode
export type GenerationResponseFromInput<T> = T extends { mode: 'generate-object', schema: infer S }
    ? S extends z.ZodSchema 
        ? z.infer<S>
        : GenerationResponse
    : GenerationResponse







////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Call Stream Text Types
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export type StreamTextAllResults = {
    text: string
    content: GenerateTextResult<ToolSet, never>['content']
    reasoning: GenerateTextResult<ToolSet, never>['reasoning']
    reasoningText: string | undefined
    files: Array<GeneratedFile>
    sources: GenerateTextResult<ToolSet, never>['sources']
    toolCalls: TypedToolCall<ToolSet>[]
    toolResults: TypedToolResult<ToolSet>[]
    finishReason: FinishReason
    usage: LanguageModelUsage
    totalUsage: LanguageModelUsage
    warnings: CallWarning[] | undefined
    steps: Array<StepResult<ToolSet>>
    request: LanguageModelRequestMetadata
    response: LanguageModelResponseMetadata & {
        messages: Array<AssistantModelMessage | ToolModelMessage>
    }
    providerMetadata: ProviderMetadata | undefined
}







////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Image & Video Types
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export type ImageAndVideoResponse = {
    mediaUrl: string
    responseAPI: string
}